import { Pencil, Plus, Printer, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { dimensionsFromRecord } from "../components/DimensionCubeFields";
import { EmptyState } from "../components/EmptyState";
import {
  ToteDetailsFields,
  toteDetailsFromRecord,
  toteDetailsPayload,
  type ToteDetailsValue
} from "../components/ToteDetailsFields";
import { Toast } from "../components/Toast";
import type { InventoryUnit } from "../types";
import { inventoryAppearance } from "../inventoryDisplay";
import { InventoryVisual } from "../components/InventoryVisual";
import type { PrinterStatus } from "../types";

export function InventoryListPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState("");
  const [printer, setPrinter] = useState<PrinterStatus | null>(null);
  const [editingUnit, setEditingUnit] = useState<InventoryUnit | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setLoading(true);
      api
        .listInventory(query)
        .then(setUnits)
        .finally(() => setLoading(false));
    }, 180);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    api.printerStatus().then(setPrinter).catch(() => setPrinter(null));
  }, []);

  async function saveToteDetails(unit: InventoryUnit, details: ToteDetailsValue) {
    setSavingEdit(true);
    setError("");
    try {
      const updated = await api.updateInventory(unit.id, {
        version: unit.version,
        ...toteDetailsPayload(details)
      });
      setUnits((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      setEditingUnit(null);
      setToast(`${updated.stock_number}: tote size and colors updated`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Tote details could not be saved");
    } finally {
      setSavingEdit(false);
    }
  }

  async function removeFromInventory(unit: InventoryUnit) {
    const confirmed = window.confirm(
      `Delete “${unit.name}” (${unit.stock_number}) from the active inventory?\n\n` +
        "It will disappear from the inventory list, but its audit history and stock number will be preserved."
    );
    if (!confirmed) return;
    setDeletingId(unit.id);
    setError("");
    try {
      await api.updateStatus(unit.id, "Archived", unit.version);
      setUnits((current) => current.filter((row) => row.id !== unit.id));
      setToast(`${unit.stock_number} removed from active inventory`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Inventory record could not be removed");
    } finally {
      setDeletingId("");
    }
  }

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <h1>Inventory</h1>
          <p>Search stock numbers, names, contents, and notes.</p>
        </div>
        <div className="heading-actions">
          <a
            className="button secondary"
            href="/api/inventory/thermal-labels.pdf"
            target="_blank"
            rel="noreferrer"
          >
            <Printer size={18} />
            Open all label PDFs
          </a>
          <Link className="button primary" to="/inventory/new">
            <Plus size={18} /> New inventory
          </Link>
        </div>
      </div>
      <label className="search-field">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search inventory"
          inputMode="search"
        />
      </label>
      {loading ? (
        <div className="loading-row">Loading inventory…</div>
      ) : units.length ? (
        <div className="unit-list">
          {units.map((unit) => (
            <div className="unit-row" key={unit.id}>
              <Link className="unit-row-link" to={`/inventory/${unit.id}`}>
                <div className="unit-thumbnail">
                  <InventoryVisual unit={unit} />
                </div>
                <div className="unit-row-main">
                  <strong>{unit.name}</strong>
                  <span>{unit.stock_number}</span>
                  {inventoryAppearance(unit) && <small>{inventoryAppearance(unit)}</small>}
                </div>
                <div className="unit-row-place">
                  <span>{unit.current_location?.name || "Location not set"}</span>
                  <small>to {unit.destination_room?.name || "destination not set"}</small>
                </div>
                <span className="status">{unit.move_status}</span>
              </Link>
              {user?.role !== "viewer" && (
                <div className="unit-row-actions">
                  {unit.inventory_type === "TOTE" && (
                    <button
                      className="unit-row-action"
                      aria-label={`Edit tote size and colors for ${unit.stock_number}`}
                      onClick={() => setEditingUnit(unit)}
                    >
                      <Pencil />
                      <span>Edit tote</span>
                    </button>
                  )}
                  {printer?.configuration.mode === "browser_pdf" ? (
                    <a
                      className="unit-row-action"
                      aria-label={`Open print-ready label for ${unit.stock_number}`}
                      href={`/api/printer/inventory/${encodeURIComponent(unit.id)}/print.pdf`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Printer />
                      <span>Print</span>
                    </a>
                  ) : (
                    <button
                      className="unit-row-action"
                      aria-label={`Print label for ${unit.stock_number}`}
                      disabled={printingId === unit.id}
                      onClick={async () => {
                        setPrintingId(unit.id);
                        try {
                          const result = await api.printInventoryLabel(unit.id);
                          setToast(`${unit.stock_number}: ${result.message}`);
                        } catch (reason) {
                          setError(
                            reason instanceof Error ? reason.message : "The label could not be sent"
                          );
                        } finally {
                          setPrintingId("");
                        }
                      }}
                    >
                      <Printer />
                      <span>{printingId === unit.id ? "Sending…" : "Print"}</span>
                    </button>
                  )}
                  {user?.role === "administrator" && (
                    <button
                      className="unit-row-action danger"
                      aria-label={`Delete ${unit.stock_number} from active inventory`}
                      disabled={deletingId === unit.id}
                      onClick={() => removeFromInventory(unit)}
                    >
                      <Trash2 />
                      <span>{deletingId === unit.id ? "Removing…" : "Delete"}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title={query ? "No matching inventory" : "No inventory yet"}
          description={query ? "Try a different stock number, name, or keyword." : "Create your first tagged moving unit."}
        />
      )}
      {toast && <Toast message={toast} tone="success" onClose={() => setToast("")} />}
      {error && <Toast message={error} tone="error" onClose={() => setError("")} />}
      {editingUnit && (
        <ToteEditDialog
          key={editingUnit.id}
          unit={editingUnit}
          saving={savingEdit}
          onSave={(details) => saveToteDetails(editingUnit, details)}
          onClose={() => setEditingUnit(null)}
        />
      )}
    </div>
  );
}

function ToteEditDialog({
  unit,
  saving,
  onSave,
  onClose
}: {
  unit: InventoryUnit;
  saving: boolean;
  onSave: (details: ToteDetailsValue) => void;
  onClose: () => void;
}) {
  const [details, setDetails] = useState(() =>
    toteDetailsFromRecord(
      unit.container_type,
      unit.container_size,
      unit.container_color,
      unit.lid_color,
      dimensionsFromRecord(unit.length, unit.width, unit.height)
    )
  );

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        className="modal-sheet inventory-tote-edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-tote-edit-title"
      >
        <header>
          <div>
            <h2 id="inventory-tote-edit-title">Edit tote size and colors</h2>
            <p>{unit.stock_number} · {unit.name}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close tote editor">
            <X />
          </button>
        </header>
        <ToteDetailsFields value={details} onChange={setDetails} />
        <div className="form-actions">
          <button className="button secondary" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="button primary" type="button" onClick={() => onSave(details)} disabled={saving}>
            {saving ? "Saving…" : "Save tote details"}
          </button>
        </div>
      </section>
    </div>
  );
}
